import { createApp } from 'vue';
import { setupI18n } from './utils/i18n';
// import Cookies from 'js-cookie';
import pinia from './store';
import router from './router';
import App from './App.vue';

import './style/index.css';
import './style/focus-optimization.css';
import './utils/rem.js';
import { registerSvgIcon } from './components/svg-icon/entry';
import { getDeviceInfo, clearDeviceInfoCache } from './utils/device';
import { getFingerprint } from './utils/fingetprint';
import { setAxiosHeader } from './utils/request';
import { initActiveConfig } from './utils/active-config';
import { createStorage, getStorage, setStorage, STORAGE_KEYS } from './utils/storage';
import * as Sentry from '@sentry/vue';
import './utils/mixpanel';
import packageJson from '../package.json';

// Performance monitoring (development only)
if (process.env.NODE_ENV === 'development') {
  import('./utils/performance-audit').then(() => {
    // Performance auditor auto-starts in development
  });
  // Memory pressure handler also only in development
  import('./utils/memory-pressure-handler').then(() => {
    // Memory pressure handler auto-starts in development
  });
}

// 可根据设备类型自动识别传入 'lg-pointer' | 'webos' | 'tizen' | 'web'
//initRemoteControl(process.env.VUE_APP_PLATFORM! as 'webos' | 'tizen' | 'web', FocusManager);

// Simple semantic version comparison: returns true if a < b
function isVersionLower(a: string, b: string): boolean {
  const pa = a.split('.').map(n => parseInt(n, 10) || 0);
  const pb = b.split('.').map(n => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const av = pa[i] ?? 0;
    const bv = pb[i] ?? 0;
    if (av < bv) return true;
    if (av > bv) return false;
  }
  return false;
}

async function bootstrap() {
  const loginGsid = createStorage(STORAGE_KEYS.GSID, null);
  loginGsid && setAxiosHeader('gsid', loginGsid);
  if (process.env.NODE_ENV !== 'production') {
    const userInfo = getStorage(STORAGE_KEYS.USER_INFO);
    const isFromLogin = getStorage(STORAGE_KEYS.IS_FROM_LOGIN);
    console.log('[DEBUG] bootstrap persisted state', {
      gsid: loginGsid,
      userInfo,
      isFromLogin,
    });
  }

  // On startup (splash): if app version is lower than 1.5.3, purge device-info and re-request.
  // Otherwise the app always reads device info from localStorage (never getDeviceInfo(false) elsewhere).
  const currentVersion = packageJson.version || '1.5.3';
  const lastVersion = getStorage<string>(STORAGE_KEYS.APP_VERSION);
  const shouldPurgeAndRerequest = !lastVersion || isVersionLower(lastVersion, '1.5.3');
  if (shouldPurgeAndRerequest) {
    clearDeviceInfoCache();
  }
  setStorage(STORAGE_KEYS.APP_VERSION, currentVersion);

  try {
    let deviceInfo: Awaited<ReturnType<typeof getDeviceInfo>>;
    if (shouldPurgeAndRerequest) {
      deviceInfo = await getDeviceInfo(false);
    } else {
      deviceInfo = await getDeviceInfo(true);
      if (deviceInfo == null) deviceInfo = await getDeviceInfo(false);
    }
    const modelName =
      process.env?.NODE_ENV === 'development' ? 'chrome_dev' : deviceInfo?.modelName || 'Unknown Device';
    setAxiosHeader('model', modelName);
    const res = await getFingerprint(deviceInfo);
    setAxiosHeader('aid', res?.aid || null);
    setAxiosHeader('vgsid', res?.vgsid || null);
    !loginGsid && setAxiosHeader('gsid', res?.vgsid || null);
    await initActiveConfig();
  } catch (error) {
    console.warn('[bootstrap] device info / fingerprint error:', error);
  }

  const app = createApp(App);
  const i18n = setupI18n();
  // --- Sentry init ---
  Sentry.init({
    app, // Vue 3 application instance
    dsn: process.env.VUE_APP_SENTRY_DSN || '',
    integrations: [
      Sentry.browserTracingIntegration({
        router,
      }),
      Sentry.replayIntegration({
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 1.0, //1.0, //0.5
    replaysSessionSampleRate: 0, // capture 10% of sessions
    replaysOnErrorSampleRate: 0, // always capture replays for errors
    environment: process.env.VUE_APP_SENTRY_ENV || process.env.NODE_ENV,
    release: process.env.VUE_APP_VERSION || 'dev',
    sendDefaultPii: true,

    // Ignore common benign errors
    ignoreErrors: [
      // Video play/pause race conditions (normal on Smart TVs)
      'AbortError: The play() request was interrupted',
      /The play\(\) request was interrupted/,
      // Video element timing issues (event fires after unmount)
      /Cannot read property 'currentTime' of null/,
      /Cannot read properties of null.*currentTime/,
      // Shaka Player internal DRM errors (non-critical)
      /Cannot read property.*of null/,
      /Cannot read properties of undefined/,
    ],

    // beforeSend(event) {
    //   // Strip sensitive headers
    //   if (event.request?.headers) {
    //     delete event.request.headers['authorization'];
    //     delete event.request.headers['cookie'];
    //   }
    //   return event;
    // },
  });

  app.use(pinia);
  app.use(router);
  app.use(i18n);

  // Register global components
  registerSvgIcon(app);

  app.mount('#app');
}
bootstrap();
